import BarcodeOnFileSheet from "./BarcodeOnFileSheet";
import { CameraOverlay, CameraPanels, CameraStage } from "./capture/CameraView";
import { BarcodeBar, BarcodeFooter } from "./capture/BarcodeStep";
import { PhotoBar, PhotoFooter, PhotoInspect } from "./capture/PhotoStep";
import { BulkPillSlot, BulkSheets } from "./capture/BulkSession";
import { DeskEnd, SavedScreen } from "./capture/SavedScreen";
import { useCaptureFlow } from "./capture/useCaptureFlow";


// CaptureCamera — one camera, one screen, one product at a time.
//
// ---------------------------------------------------------------------------
// WHAT THIS IS FOR, AND WHY IT IS NOT QuickScanner
// ---------------------------------------------------------------------------
// QuickScanner is the embedded-admin surface. Inside the Shopify mobile webview
// getUserMedia is blocked outright, so it acquires a barcode through the native
// OS scanner and photographs through a file input — two separate excursions out
// of the page, each of which backgrounds the WebView and each of which has cost
// this project a bug (the picker dismissal reading as a backdrop tap, #84; the
// scanner dismissal doing the same, #88).
//
// This page is a TOP-LEVEL DOCUMENT in a plain mobile browser. That single
// difference is what the whole design rests on: we may hold a camera stream, so
// the barcode and the photographs come off the SAME stream, with no excursion,
// no phase change and nothing to dismiss. There is no modal to accidentally
// close because there is no modal.
//
// ---------------------------------------------------------------------------
// THE MODEL
// ---------------------------------------------------------------------------
//   scanning  the stream is live and liveBarcode is reading frames. Header says
//             what it is looking for. The shutter is inert.
//   locked    a barcode was read. Header shows it. The shutter takes stills,
//             up to MAX_SHOTS, each appearing as a removable tile.
//             At MIN_TO_COMMIT the header grows an Add button.
//   -> Add    files the pair, clears the deck and returns to `scanning`.
//
// Capture is DECOUPLED FROM ENRICHMENT on purpose. Today's Quick Scan enqueues a
// run on Done and the merchant waits on model outcomes; walking a 33,000-product
// shelf that way is not a thing a person can do. Here the loop is barcode ->
// photos -> next, at the speed of a hand, and enrichment reads the pile later.
//
// ---------------------------------------------------------------------------
// WHERE THE PHOTOGRAPHS GO
// ---------------------------------------------------------------------------
// Confirm files them for real: describe, plan presigned PUTs, PUT the bytes
// straight to S3, commit the keys. Nothing is held in memory past the commit
// and no photograph passes through a server of ours.
//
// The shop is NOT chosen here and cannot be. This page holds a cookie; the
// backend resolves that cookie to a shop by reading the database, so there is
// nowhere in a request for a tenant to be written. `shop` arrives as a prop
// purely so the notice can name it honestly.
//
// ⚠️ This block used to say "Nothing uploads. Committed products live in memory
// for the length of the session." That was true for about a day. A comment
// describing an earlier state of the code is worse than no comment: the next
// person reads it, believes the upload path does not exist, and goes looking
// for the bug somewhere else entirely.



// No backticks anywhere in here: this is a template literal, and one inside a
// comment closes it. The same warning is on QuickScanner's stylesheet.
const CAPTURE_CSS = `
html,body{margin:0; padding:0; background:#000; overflow:hidden;}

.cap-root{
  position:fixed; top:0; left:0; width:100%;
  /* A mobile browser sizes a fixed inset:0 box to the LARGE viewport - the one
     you get once the URL bar and the bottom nav have retracted. With that
     chrome on screen, which is exactly how the page opens, the bottom of the
     box sits behind the browser's own toolbar and takes the shutter and the
     tile strip with it. 100dvh is the CURRENTLY VISIBLE viewport and tracks
     that chrome as it comes and goes; 100vh stays as the fallback for anything
     too old to know the unit. Measured on Brave/Android, where the bottom nav
     is about 90px of otherwise-invisible loss. */
  height:100vh; height:100dvh;
  background:#000; color:#fff;
  display:flex; flex-direction:column;
  font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;
  /* ---------------------------------------------------------------------
     THE SCALE IS VIEWPORT-RELATIVE, NOT FIXED PIXELS.
     ---------------------------------------------------------------------
     Fixed px assume the CSS viewport matches the physical screen. On a phone
     that is often false: a browser in desktop mode, or one that ignores the
     viewport meta, lays the page out at ~980px and then scales it down to the
     ~411px the screen actually has - shrinking every fixed size by about 2.4x.
     Measured against real device screenshots, that is exactly what was
     happening here, and it is why raising the px values barely moved anything.

     A vw unit is a percentage of the viewport, so it survives that: 22vw is
     22% of the screen whether the layout is 411px or 980px wide. Each value is
     clamped at both ends so a narrow phone stays legible and this never runs
     away on a wide screen. min() would defeat the point - it would re-cap the
     desktop-width case back to the small size. */
  --fs-label:clamp(12px, 3.2vw, 26px);
  --fs-body: clamp(17px, 4.6vw, 38px);
  --fs-hint: clamp(19px, 5.4vw, 44px);
  /* Sized so a 13-digit EAN fits BESIDE the Add and discard buttons rather
     than ellipsing into "50605178836...". A truncated barcode is the one
     failure this screen cannot have: it is what the next photographs get
     filed against, and two products in a range often differ only in the last
     two digits. Legible beats large here. */
  --fs-code: clamp(20px, 5.6vw, 50px);
  --fs-title:clamp(32px, 10vw, 82px);
  --fs-btn:  clamp(17px, 4.8vw, 38px);
  --tap:     clamp(54px, 14vw, 108px);
  --shutter: clamp(84px, 22vw, 176px);
  /* Trimmed from 21vw. A tile is a thumbnail to recognise a shot by and a
     target to tap for a proper look — it was never going to show whether the
     fine print is legible, and that job now belongs to the inspector. Every
     row of tile height is a row off the viewfinder. */
  --tile:    clamp(64px, 17vw, 136px);
  --pad:     clamp(16px, 4.2vw, 34px);

  font-size:var(--fs-body); line-height:1.4;
  overscroll-behavior:none; -webkit-user-select:none; user-select:none;
}
/* THE VIDEO IS INSET TO THE SPACE BETWEEN THE HEADER AND THE FOOTER.
   The stage used to be the whole viewport with the chrome floating on top, so
   the header and the shutter bar covered roughly the top and bottom 14% of the
   frame. Every one of those hidden pixels was still being written to the file —
   the merchant simply could not see what they were composing. The insets are
   measured from the real elements at runtime (they change: the barcode wraps to
   a second line, the uploader line appears and goes), not guessed at, because a
   hard-coded value is wrong on the first phone that disagrees. */
.cap-stage{
  position:relative; flex:1; min-height:0; overflow:hidden;
  --chrome-top:0px; --chrome-bottom:0px;
}
/* ⚠️ contain, NOT cover, AND THAT IS A CORRECTNESS FIX RATHER THAN A TASTE ONE.
   With cover the browser CROPS the sensor frame to fill the screen, while
   captureBestFrame draws the FULL videoWidth x videoHeight to its canvas. So
   the viewfinder and the saved file were two different framings: the file held
   2-17% more of the scene than the merchant composed, on edges that changed per
   handset — and, because the stage is 100dvh, changed again mid-session as the
   URL bar retracted. A merchant lining a pack up against what they can see had
   no way to know what was actually being kept.
   contain letterboxes instead: black bars, and what you see is what is saved.
   The saved dimensions do not change and no pixels are lost — cropping the
   CAPTURE to match the preview would have been the other way to make these
   agree, and it would have thrown away 2-17% of every photograph on an axis
   that moves, including the fine print the enrichment call is paid to read. */
/* ⚠️ A WRAPPER, because the insets cannot size the <video> directly.
   Setting top/bottom plus height:auto on an absolutely positioned REPLACED
   element does not stretch it: height:auto resolves to the intrinsic height and
   the bottom offset is dropped as over-constrained. Measured, not assumed: the
   video
   came out 732px tall in a 541px slot and ran under the shutter bar. The
   wrapper is a plain box, so the offsets mean what they say, and the video
   fills it. */
.cap-viewport{
  position:absolute;
  top:var(--chrome-top); bottom:var(--chrome-bottom); left:0; right:0;
}
.cap-video{
  position:absolute; inset:0; width:100%; height:100%;
  object-fit:contain; background:#000; display:block;
  transition:opacity .18s ease;
}
.cap-video--wait{opacity:.01;}
.cap-video{transition:opacity .2s ease;}
/* iOS draws its own round play/pause button over an inline video; the camera is never a player.
   Taps go to the tap zones above, so the video takes none. One rule per pseudo-element:
   a browser drops a whole rule over one it does not know. */
.cap-video,.cap-backdrop{pointer-events:none;}
.cap-video.cap-vid-paused,.cap-stage--photo .cap-backdrop.cap-vid-paused{opacity:.01 !important;}
.cap-video::-webkit-media-controls,.cap-backdrop::-webkit-media-controls{display:none !important;}
.cap-video::-webkit-media-controls-panel,.cap-backdrop::-webkit-media-controls-panel{display:none !important;}
.cap-video::-webkit-media-controls-overlay-play-button,.cap-backdrop::-webkit-media-controls-overlay-play-button{display:none !important;}
.cap-video::-webkit-media-controls-start-playback-button,.cap-backdrop::-webkit-media-controls-start-playback-button{display:none !important;}
/* The calm dark stand-in for the camera while it opens; fades out as the video fades in. */
.cap-ph{position:absolute; inset:0; background:radial-gradient(120% 80% at 50% 35%, #1b2024 0%, #0d1012 70%); transition:opacity .2s ease; pointer-events:none;}
.cap-ph--off{opacity:0;}
.cap-cammsg{
  position:absolute; z-index:3; display:flex; align-items:center; justify-content:center;
  left:calc(var(--fx) + var(--fw) / 2); top:calc(var(--fy) + var(--fh) / 2);
  transform:translate(-50%,-50%); width:calc(var(--fw) * .9); height:calc(var(--fh) * .3);
  transition:opacity .2s ease; pointer-events:none;
}
.cap-cammsg--off{opacity:0;}
/* On the scan page it sits exactly in the reticle, which is placed by the screen, not the frame. */
.cap-stage--scan .cap-cammsg{left:50%; top:var(--ry); width:var(--rw); height:calc(var(--rw) * .6);}
/* Camera start states: one glass pill, or one glyph and a line. No boxes. */
.cap-cpill{
  display:inline-flex; align-items:center; height:34px; padding:0 16px; border-radius:17px;
  background:rgba(0,0,0,.6); color:#f4f2ee; font-size:13px; font-weight:500;
  -webkit-backdrop-filter:blur(10px); backdrop-filter:blur(10px); animation:cap-sfade .25s ease-out both;
}
.cap-cmsg{
  display:flex; flex-direction:column; align-items:center; gap:10px; max-width:280px; text-align:center;
  color:#f4f2ee; font-size:15px; font-weight:600; line-height:20px; animation:cap-sfade .25s ease-out both;
}
.cap-cmsg svg{opacity:.9;}
.cap-cmsg small{font-size:13px; font-weight:400; line-height:18px; color:rgba(244,242,238,.68);}
.cap-tapzone{
  position:absolute; inset:0; z-index:1; border:0; padding:0; margin:0; background:transparent; font:inherit;
  -webkit-tap-highlight-color:transparent; cursor:pointer;
}
.cap-tapzone--solid{z-index:30; background:#0b0f0e; display:flex; flex-direction:column; align-items:center; justify-content:center;}
/* Waiting: the corners breathe, the scan line rests. */
.cap-reticle--wait i{animation:cap-breathe 2.6s ease-in-out infinite;}
.cap-reticle--wait b{display:none;}
.cap-reticle--still b{display:none;}
@keyframes cap-breathe{0%,100%{opacity:.5; transform:scale(.96);} 50%{opacity:1; transform:scale(1);}}
.cap-reticle i:nth-child(1){transform-origin:0 0;} .cap-reticle i:nth-child(2){transform-origin:100% 0;}
.cap-reticle i:nth-child(3){transform-origin:0 100%;} .cap-reticle i:nth-child(4){transform-origin:100% 100%;}
/* The last frame, blurred, held over the new picture until it arrives (resume, flip). */
.cap-snapshot{
  position:absolute; inset:0; width:100%; height:100%; object-fit:cover; display:block; pointer-events:none;
  filter:blur(16px); opacity:0; transition:opacity .35s ease; z-index:0;
}
.cap-snapshot--on{opacity:1;}
.cap-snapshot--mirror{transform:scaleX(-1);}
.cap-stage--photo .cap-snapshot{object-fit:contain;}
/* No camera: the three ways in are the screen. */
.cap-stage--none .cap-scanbot{bottom:calc(50vh - 80px); min-height:0; background:none; padding-bottom:0; gap:28px;}
.cap-stage--none .cap-sopt span{width:68px; height:68px; border-radius:34px;}
.cap-scanbot-none{text-align:center; font-size:15px; font-weight:600; color:#f4f2ee;}
@media (prefers-reduced-motion:reduce){.cap-reticle--wait i{animation:none;}}
.cap-video--mirror{transform:scaleX(-1);}

/* THE CAPTURE AREA, DRAWN WHERE IT ACTUALLY IS.
   object-fit:contain letterboxes, so the painted image is smaller than the
   element and the black bars are INSIDE it — there is no CSS that outlines the
   picture rather than the box. The rect is therefore computed from
   videoWidth/videoHeight against the element's own size and set as custom
   properties. Everything inside this outline is what gets saved. Nothing
   outside it is.
   Drawn as a hairline plus corner ticks rather than a heavy border: it has to
   be unambiguous without competing with the product for attention. */
.cap-framearea{
  position:absolute; z-index:2; pointer-events:none;
  left:var(--fx); top:var(--fy); width:var(--fw); height:var(--fh);
  outline:1px solid rgba(255,255,255,.5);
  outline-offset:-1px;
  animation:cap-sfade .2s ease-out both;
}
.cap-framearea::before, .cap-framearea::after{
  content:""; position:absolute; width:26px; height:26px;
  border:3px solid rgba(255,255,255,.92); border-radius:3px;
}
.cap-framearea::before{top:-1px; left:-1px; border-right:0; border-bottom:0;}
.cap-framearea::after{bottom:-1px; right:-1px; border-left:0; border-top:0;}

/* The two corners the pseudo-elements cannot reach. */
.cap-framearea i{
  position:absolute; width:26px; height:26px;
  border:3px solid rgba(255,255,255,.92); border-radius:3px;
}
.cap-framearea i:first-child{top:-1px; right:-1px; border-left:0; border-bottom:0;}
.cap-framearea i:last-child{bottom:-1px; left:-1px; border-right:0; border-top:0;}

/* --- header ------------------------------------------------------------- */
/* The top chrome is a FLOW STACK, not two absolutely-positioned boxes.
   The notice used to be pinned at a hand-computed offset below the header,
   which meant any change to the header's height put them on top of each other
   - and the header does change height: when the barcode cannot fit beside the
   buttons it wraps to a second row, and the discard button disappeared behind
   the notice. Stacking them removes the guess. */
.cap-top{
  position:absolute; top:0; left:0; right:0; z-index:3;
  background:linear-gradient(to bottom,rgba(0,0,0,.84),rgba(0,0,0,.6) 62%,rgba(0,0,0,0));
  padding-bottom:calc(var(--pad) * 1.4);
  transition:background .18s ease-out;
}
/* Locking a barcode changes the CHROME, not just the copy. Scanning is a dark
   translucent gradient over the viewfinder; locked is a solid accent slab. The
   two states are now unmistakable from across a stockroom, in peripheral
   vision, which is where this actually gets read - the merchant is looking at
   the shelf, not the screen. */
.cap-top--locked{
  background:linear-gradient(to bottom,#0B7A63 0%,#0B7A63 78%,rgba(11,122,99,0) 100%);
}
@media (prefers-reduced-motion:reduce){ .cap-top{transition:none;}
}

/* The lock moment itself. A one-shot wash of the accent colour over the whole
   viewfinder - the confirmation a shutter sound would give you if a web page
   could make one without permission. */
.cap-flash{
  position:absolute; inset:0; z-index:6; pointer-events:none;
  background:#19C39B; opacity:0;
  animation:capflash .18s ease-out forwards;
}
@keyframes capflash{ 0%{opacity:.8} 100%{opacity:0} }
@media (prefers-reduced-motion:reduce){ .cap-flash{animation:none; opacity:0;}
}

.cap-check{
  display:inline-block; margin-right:.4em; font-weight:700;
}

/* The shutter blink. White and brief - this is the "I heard you" for the tap
   itself, distinct from the green wash that means a barcode locked. */
.cap-snap{
  position:absolute; inset:0; z-index:6; pointer-events:none;
  background:#fff; opacity:0;
  animation:capsnap .26s ease-out forwards;
}
@keyframes capsnap{ 0%{opacity:.85} 100%{opacity:0} }
@media (prefers-reduced-motion:reduce){ .cap-snap{animation:none; opacity:0;}
}

/* Upload confirmation. Sits above the footer, names the barcode, and goes away
   on its own - it must not become one more thing to dismiss between products. */
.cap-filed{
  position:absolute; z-index:5; left:var(--pad); right:var(--pad);
  bottom:calc(var(--shutter) + var(--pad) * 4.5);
  display:flex; align-items:center; justify-content:center; flex-wrap:wrap;
  gap:.1em;
  padding:calc(var(--pad) * .7) calc(var(--pad) * .9);
  border-radius:999px; text-align:center;
  background:#0B7A63; color:#fff;
  font-size:var(--fs-label); line-height:1.3;
  box-shadow:0 6px 22px rgba(0,0,0,.45);
  animation:capfiled .22s ease-out;
}
.cap-filed b{
  font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
  font-weight:600; margin:0 .35em;
}
@keyframes capfiled{ 0%{opacity:0; transform:translateY(8px)} 100%{opacity:1; transform:none} }
@media (prefers-reduced-motion:reduce){ .cap-filed{animation:none;}
}
/* The min-width is the load-bearing part, not the basis. A flex item with the
   default shrink:1 will happily squeeze below its basis and ellipse, which is
   what "50605178836..." was. Flooring the width at roughly 13 monospace digits
   (0.6em per char, plus slack) means the barcode CANNOT shrink - so when the
   buttons no longer fit beside it the header wraps to a second row instead,
   and every digit stays on screen. */
/* basis 0, NOT auto. Flexbox decides whether to wrap using each item's
   hypothetical main size - its flex-basis - and only shrinks afterwards. With
   basis:auto this box reports the width of its longest line (the step label),
   which pushed the buttons onto a second row even though there was room for
   them once it shrank. Basis 0 never triggers the wrap; min-width still stops
   it squeezing the barcode into an ellipsis. */
.cap-bc{flex:1 1 0; min-width:calc(var(--fs-code) * 8.2);}

/* --- controls ----------------------------------------------------------- */
.cap-btn{
  appearance:none; border:0; border-radius:999px; font:inherit; font-weight:600;
  font-size:var(--fs-btn); padding:0 calc(var(--pad) * 1.6);
  min-height:var(--tap); background:#fff;
  color:#101410; flex:0 0 auto; cursor:pointer;
}
.cap-btn:disabled{opacity:.4; cursor:default;}
.cap-btn--ghost{background:rgba(255,255,255,.18); color:#fff;}
.cap-root button:focus-visible{outline:2px solid #67E8C4; outline-offset:2px;}

/* --- notice ------------------------------------------------------------- */

/* --- scanning step (bulk and quick, before a code is read) -------------- */
/* Top: a bar over a transparent .cap-top, so the queue row stays in flow
   beneath it. Bottom: no sheet, a fade with the "can't scan it" options. The
   old header and the shutter row stay mounted (the measurer reads their
   refs) but hidden; they return in the locked state untouched. */
.cap-top--scan{background:none; padding:calc(env(safe-area-inset-top,0px) + 16px) 16px 0;}
.cap-top--scan .cap-queue{margin-top:10px;}
.cap-sbar{display:grid; grid-template-columns:minmax(44px,auto) minmax(0,1fr) minmax(44px,auto); column-gap:12px; align-items:center; min-height:44px; color:#f4f2ee;}
.cap-sbtn{
  appearance:none; border:0; padding:0; font:inherit; color:#fff; cursor:pointer;
  width:44px; height:44px; border-radius:22px; background:rgba(255,255,255,.1);
  display:inline-flex; align-items:center; justify-content:center;
  -webkit-tap-highlight-color:transparent;
  transition:transform .12s ease-out, background-color .16s ease, color .16s ease;
}
.cap-sbtn--on{background:#fff; color:#101410;}
.cap-sbtn:disabled{opacity:.5; cursor:default;}
.cap-sbtn:active:not(:disabled), .cap-sopt:active:not(:disabled){transform:scale(.94);}
.cap-sbtn:focus:not(:focus-visible), .cap-sopt:focus:not(:focus-visible){outline:none;}
.cap-sback{font-size:30px; line-height:1; padding-bottom:3px;}
.cap-stitle{display:flex; flex-direction:column; align-items:center; text-align:center; min-width:0;}
.cap-stitle span{max-width:100%; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;}
/* The title is the check that this is the right item: two lines before it is cut. */
.cap-stitle span:first-child{font-size:15px; line-height:20px; font-weight:600; color:#f4f2ee; white-space:normal; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical;}
.cap-stitle span:last-child{font-size:12px; line-height:16px; color:#a8a39a;}
.cap-sright{justify-self:end; display:inline-flex; gap:8px;}
.cap-spill{
  position:absolute; z-index:2; left:0; right:0; display:flex; justify-content:center; pointer-events:none;
  top:calc(var(--fy) + var(--fh) / 2 + var(--fh) * .15 + 24px);
}
.cap-spill span{
  display:inline-flex; align-items:center; height:34px; padding:0 16px; border-radius:17px;
  background:rgba(0,0,0,.6); color:#f4f2ee; font-size:13px; font-weight:500;
}
/* Scanning fills the screen. Nothing is saved yet, so the picture need not be
   letterboxed to show its edges; the bars only made the page look broken. The
   reticle sits in the upper third, where a phone held at chest height aims,
   and the reader is told where it is (coverBand). */
.cap-stage--scan{--rw:min(80vw,360px); --ry:36%;}
.cap-stage--scan .cap-viewport{top:0; bottom:0;}
.cap-stage--scan .cap-video{object-fit:cover;}
.cap-stage--scan .cap-top--scan{background:linear-gradient(to bottom,rgba(0,0,0,.6),rgba(0,0,0,0)); padding-bottom:28px;}
.cap-stage--scan .cap-reticle{
  left:50%; top:var(--ry); width:var(--rw); height:calc(var(--rw) * .6); --rh:calc(var(--rw) * .6);
}
.cap-stage--scan .cap-spill{top:calc(var(--ry) + var(--rw) * .3 + 24px);}
/* iPhone Safari only, after more than 3 visits in a row asked: how to stop the camera question.
   As wide as the reticle; a tap hides it for good. */
.cap-allowtip{
  position:absolute; z-index:2; left:50%; transform:translateX(-50%); top:calc(var(--ry) + var(--rw) * .3 + 70px);
  box-sizing:border-box; width:var(--rw); display:flex; align-items:flex-start; gap:12px; padding:10px 10px 10px 14px;
  border:0; border-radius:14px; background:rgba(0,0,0,.62); color:#f4f2ee; font-family:inherit; text-align:left;
  animation:cap-sfade .25s ease-out both;
}
.cap-allowtip-txt{flex:1; min-width:0; display:flex; flex-direction:column; gap:4px;}
.cap-allowtip-txt strong{font-size:13px; line-height:17px; font-weight:600;}
.cap-allowtip-txt span{font-size:12px; line-height:16px; font-weight:500; opacity:.88;}
.cap-allowtip kbd{font:inherit; font-weight:700; padding:1px 5px; border-radius:4px; background:rgba(255,255,255,.2);}
.cap-allowtip b{font-size:18px; line-height:17px; font-weight:400; opacity:.7; padding:0 2px;}
.cap-top--hide{visibility:hidden;}
/* Bulk's pill floats under the bar; on the photo step this room keeps it off the saved-area outline. */
.cap-bpgap{height:58px;}
.cap-filed--scan{top:calc(var(--chrome-top,0px) + 8px); bottom:auto;}
.cap-foot--scan{padding:0; height:0; background:none;}
.cap-foot--scan > :not(.cap-scanbot){display:none;}
.cap-scanbot{
  position:absolute; left:0; right:0; bottom:0; min-height:240px; box-sizing:border-box;
  padding:0 28px calc(env(safe-area-inset-bottom,0px) + 40px);
  background:linear-gradient(to top,rgba(0,0,0,.85),rgba(0,0,0,0));
  display:flex; flex-direction:column; justify-content:flex-end; gap:16px;
  padding-bottom:calc(env(safe-area-inset-bottom,0px) + 40px + var(--kb, 0px));
}
.cap-scanbot--typing{padding-bottom:calc(16px + var(--kb, 0px));}
.cap-scanbot .cap-error{margin:0; animation:cap-sfade .18s ease-out both;}
.cap-swap{animation:cap-sswap .18s ease-out both;}
@keyframes cap-sfade{from{opacity:0;} to{opacity:1;}}
@keyframes cap-sswap{from{opacity:0; transform:translateY(8px);} to{opacity:1; transform:none;}}
@keyframes cap-spulse{0%,100%{opacity:1;} 50%{opacity:.45;}}
@keyframes cap-ssweep{
  from{transform:translateY(calc(var(--rh) * -.5 + 14px));}
  to{transform:translateY(calc(var(--rh) * .5 - 14px));}
}
.cap-scanbot-lead{text-align:center; font-size:13px; line-height:18px; color:#d9d5cd;}
.cap-file{display:none;}
.cap-sopts{display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); justify-items:center;}
.cap-sopt{
  appearance:none; border:0; background:none; padding:0; font:inherit; color:#fff; cursor:pointer;
  min-width:64px; display:flex; flex-direction:column; align-items:center; gap:8px; font-size:12px; font-weight:600;
  -webkit-tap-highlight-color:transparent;
  transition:transform .12s ease-out;
}
.cap-sopt:disabled{opacity:.5; cursor:default;}
.cap-sopt span{
  width:60px; height:60px; border-radius:30px; background:rgba(255,255,255,.12);
  display:inline-flex; align-items:center; justify-content:center;
}
.cap-sopt:disabled span{animation:cap-spulse 1s ease-in-out infinite;}
@media (prefers-reduced-motion:reduce){
  .cap-sbtn, .cap-sopt{transition:none;}
  .cap-sbtn:active:not(:disabled), .cap-sopt:active:not(:disabled){transform:none;}
  .cap-swap, .cap-scanbot .cap-error, .cap-sopt:disabled span, .cap-reticle b{animation:none;}
  .cap-video{transition:none;}
}
.cap-typeform{display:flex; align-items:center; gap:8px; min-height:60px;}
.cap-typewrap{position:relative; flex:1 1 auto; min-width:0; display:flex;}
.cap-typein{
  flex:1 1 auto; min-width:0; width:100%; height:48px; box-sizing:border-box; font:inherit; font-size:16px;
  border:0; border-radius:24px; padding:0 62px 0 20px; background:#fff; color:#101410; text-transform:uppercase;
}
.cap-typein::placeholder{text-transform:none; color:#8a8780;}
.cap-typemode{
  appearance:none; border:0; position:absolute; right:6px; top:6px; height:36px; min-width:46px; padding:0 8px;
  border-radius:18px; background:#ecebe6; color:#101410; font:inherit; font-size:13px; font-weight:700; cursor:pointer;
}
.cap-typego{
  appearance:none; border:0; flex:none; width:48px; height:48px; border-radius:24px; background:#fff; color:#101410;
  display:flex; align-items:center; justify-content:center; cursor:pointer;
}
.cap-typego:disabled{opacity:.45; cursor:default;}
.cap-typeaway{position:absolute; inset:0; z-index:2; border:0; padding:0; margin:0; background:transparent; -webkit-tap-highlight-color:transparent;}

/* --- photo step (bulk and quick, after the barcode step) ---------------- */
/* The scan bar on top, the scan fade below; the picture keeps its framing. */
.cap-backdrop{display:none;}
.cap-stage--photo .cap-backdrop{
  display:block; position:absolute; inset:0; width:100%; height:100%; object-fit:cover;
  filter:blur(28px) brightness(.55); transform:scale(1.12); pointer-events:none;
}
.cap-stage--photo .cap-video{background:transparent;}
/* Under the photo viewer the blurred backdrop (a full-screen blur, every frame) is not drawn: it made swiping lag. */
.cap-root--viewing .cap-backdrop{display:none !important;}
.cap-top--photo{background:linear-gradient(to bottom,rgba(0,0,0,.6),rgba(0,0,0,0)); padding-bottom:28px;}
.cap-foot--photo{gap:12px; padding:24px 0 calc(env(safe-area-inset-bottom,0px) + 20px); background:linear-gradient(to top,rgba(0,0,0,.85) 70%,rgba(0,0,0,0));}
.cap-foot--photo .cap-spill{position:static; top:auto; display:flex; justify-content:center;}
.cap-foot--photo .cap-spill span{display:inline-flex; align-items:center; height:34px; padding:0 16px; border-radius:17px; background:rgba(0,0,0,.6); color:#f4f2ee; font-size:13px; font-weight:500;}
.cap-foot--photo .cap-error{margin:0 16px;}
.cap-pstrip{
  display:flex; gap:8px; align-items:center; overflow-x:auto; padding:0 16px; height:64px;
  scroll-snap-type:x proximity; scroll-padding:0 16px; scrollbar-width:none; -webkit-overflow-scrolling:touch; overscroll-behavior-x:contain;
  -webkit-mask-image:linear-gradient(to right,transparent 0,#000 16px,#000 calc(100% - 16px),transparent 100%);
  mask-image:linear-gradient(to right,transparent 0,#000 16px,#000 calc(100% - 16px),transparent 100%);
  animation:cap-sswap .2s ease-out both;
}
.cap-pstrip::-webkit-scrollbar{display:none;}
/* Centred while it fits, scrollable from the first tile once it does not. */
.cap-pstrip > :first-child{margin-left:auto;}
.cap-pstrip > :last-child{margin-right:auto;}
.cap-pt{
  position:relative; flex:0 0 56px; width:56px; height:56px; box-sizing:border-box; border-radius:12px; overflow:hidden;
  background:rgba(255,255,255,.14); scroll-snap-align:start; padding:0; border:0;
}
.cap-pt img{width:100%; height:100%; object-fit:cover; display:block;}
.cap-pt--pack{background:linear-gradient(180deg,#fff,#f3f1ec);}
.cap-pt--pack img{object-fit:contain;}
.cap-pt--new{box-shadow:0 0 0 2px #f2b01e;}
.cap-pt--pending img{opacity:.7;}
.cap-pt--dash{border:1.5px dashed #6a6760; background:none; display:flex; align-items:center; justify-content:center; color:#a8a39a; cursor:pointer; font:inherit; font-size:22px;}
.cap-pt--front{border-color:#f2b01e; color:#f2b01e; font-size:11px; font-weight:600; flex-direction:column; gap:2px; cursor:default;}
/* The tile stays a clean rounded square. Only the invisible remove target pokes past its top-right corner,
   layered above the open button, so taps there remove and everywhere else inspect. */
.cap-pt--shot{overflow:visible; background:none;}
.cap-pt-open{
  position:absolute; inset:0; padding:0; border:0; background:rgba(255,255,255,.14); cursor:pointer; display:block; width:100%; height:100%;
  border-radius:12px; overflow:hidden;
}
.cap-pt--shot .cap-pt-tag{border-radius:0 0 12px 12px;}
.cap-pt-tag{
  position:absolute; left:0; right:0; bottom:0; text-align:center; font-size:9px; line-height:13px; font-weight:600;
  background:rgba(0,0,0,.65); color:#fff; white-space:nowrap; overflow:hidden;
}
.cap-pt-tag--new{background:#f2b01e; color:#101410;}
.cap-pt-x{
  position:absolute; z-index:2; top:-4px; right:-4px; width:44px; height:44px; padding:0; border:0; background:none;
  cursor:pointer; -webkit-tap-highlight-color:transparent;
}
.cap-pt-x::before{
  content:""; position:absolute; top:4px; right:4px; width:36px; height:36px; border-radius:0 12px 0 0; pointer-events:none;
  background:radial-gradient(circle at top right, rgba(0,0,0,.55), transparent 70%);
}
.cap-pt-x svg{position:absolute; top:10px; right:10px; width:12px; height:12px; filter:drop-shadow(0 1px 1.5px rgba(0,0,0,.6)); pointer-events:none;}
.cap-pt-off{
  position:absolute; left:3px; top:3px; width:16px; height:16px; border-radius:8px; background:rgba(0,0,0,.7);
}
.cap-pt-off::after{content:""; position:absolute; left:4.5px; top:4.5px; width:7px; height:7px; border-radius:4px; background:#ffb020;}
.cap-pt-wait{
  position:absolute; left:50%; top:50%; width:18px; height:18px; margin:-9px; border-radius:999px;
  border:2px solid rgba(255,255,255,.35); border-top-color:#fff; animation:capspin .7s linear infinite;
}
.cap-pdiv{flex:0 0 1px; width:1px; height:40px; background:rgba(255,255,255,.25);}
.cap-prow{display:grid; grid-template-columns:minmax(0,1fr) auto minmax(0,1fr); column-gap:8px; align-items:center; padding:0 16px; max-width:560px; width:100%; box-sizing:border-box; margin:0 auto;}
.cap-pup{
  appearance:none; border:0; background:none; padding:0; font:inherit; color:#fff; cursor:pointer; justify-self:start;
  display:flex; flex-direction:column; align-items:center; gap:6px; font-size:12px; font-weight:600;
  transition:transform .12s ease-out; -webkit-tap-highlight-color:transparent;
}
.cap-pup span{width:52px; height:52px; border-radius:26px; background:rgba(255,255,255,.12); display:inline-flex; align-items:center; justify-content:center;}
.cap-pshut{
  width:76px; height:76px; border-radius:38px; padding:0; background:#fff; border:4px solid rgba(255,255,255,.34);
  background-clip:padding-box; cursor:pointer; transition:transform .12s ease-out;
}
.cap-pshut:disabled{opacity:.32; cursor:default;}
.cap-pdone{
  appearance:none; border:0; font:inherit; font-size:14px; font-weight:700; height:48px; padding:0 16px; border-radius:24px;
  justify-self:end; background:#f2b01e; color:#1b1a18; cursor:pointer; white-space:nowrap;
  transition:transform .12s ease-out, background-color .16s ease, color .16s ease;
}
.cap-pdone:disabled{background:rgba(255,255,255,.22); color:rgba(255,255,255,.6); cursor:default;}
.cap-pup:active, .cap-pt--dash:active:not(.cap-pt--front){transform:scale(.94);}
.cap-pshut:active:not(:disabled){transform:scale(.94);}
.cap-pdone:active:not(:disabled){transform:scale(.96);}
.cap-pfade{animation:cap-sfade .16s ease-out both;}
@media (max-width:359px){
  .cap-prow{grid-template-columns:52px auto minmax(0,1fr); column-gap:6px; padding:0 12px;}
  .cap-pshut{width:68px; height:68px; border-radius:34px;}
  .cap-pdone{font-size:13px; padding:0 10px; min-width:0; max-width:100%; overflow:hidden; text-overflow:ellipsis;}
}
@media (prefers-reduced-motion:reduce){
  .cap-framearea, .cap-pstrip{animation:none;}
  .cap-pup, .cap-pshut, .cap-pdone{transition:none;}
  .cap-pup:active, .cap-pshut:active:not(:disabled), .cap-pdone:active:not(:disabled), .cap-pt--dash:active{transform:none;}
  .cap-pfade, .cap-pt-wait{animation:none;}
}

/* --- reticle ------------------------------------------------------------ */
/* ⚠️ POSITIONED AND SIZED OFF THE CAPTURE AREA, not the screen.
   It was centred on the stage and sized in vw, which was fine while the video
   filled the screen. Now that the picture is inset and letterboxed, a reticle
   centred on the viewport sits off-centre in the frame and is WIDER than the
   picture it is drawn on — telling the merchant to aim at something partly
   outside the photograph. The custom properties are the painted rect, so these
   track it exactly, on every handset and through every rotation.
   0.9 of the width is not a look: it is the same fraction liveBarcode crops to
   before decoding, so the box the merchant aims at and the band the scanner
   actually reads are the same box. */
.cap-reticle{
  position:absolute; z-index:2; pointer-events:none; /* a tap on the frame reaches the tap zone under it */
  left:calc(var(--fx) + var(--fw) / 2);
  top:calc(var(--fy) + var(--fh) / 2);
  transform:translate(-50%,-50%);
  width:calc(var(--fw) * .9); height:calc(var(--fh) * .3); --rh:calc(var(--fh) * .3);
  box-shadow:0 0 0 100vmax rgba(0,0,0,.25); border-radius:14px;
}
/* Corner ticks and one yellow scan line, the only accent on the page. */
.cap-reticle i{position:absolute; width:36px; height:36px; border:0 solid #fff;}
.cap-reticle i:nth-child(1){left:0; top:0; border-width:3px 0 0 3px; border-top-left-radius:14px;}
.cap-reticle i:nth-child(2){right:0; top:0; border-width:3px 3px 0 0; border-top-right-radius:14px;}
.cap-reticle i:nth-child(3){left:0; bottom:0; border-width:0 0 3px 3px; border-bottom-left-radius:14px;}
.cap-reticle i:nth-child(4){right:0; bottom:0; border-width:0 3px 3px 0; border-bottom-right-radius:14px;}
.cap-reticle b{
  position:absolute; left:20px; right:20px; top:calc(50% - 1px); height:2px;
  background:#f2b01e; box-shadow:0 0 12px rgba(242,176,30,.8);
  animation:cap-ssweep 2s ease-in-out infinite alternate;
}

/* --- photo viewer -------------------------------------------------------- */
/* Full screen ON TOP of the running camera, never instead of it; the frontmost thing while open. */
.cap-inspect{
  --col:max(var(--pad), calc((100% - 560px) / 2));
  position:absolute; inset:0; z-index:8; background:#0b0d0e; color:#f4f2ee;
  display:flex; flex-direction:column; overflow:hidden;
}
.cap-v-bar{
  flex:0 0 auto; display:flex; align-items:center; justify-content:space-between; gap:var(--pad);
  padding:calc(env(safe-area-inset-top,0px) + 12px) var(--col) 8px;
}
.cap-v-x{
  width:44px; height:44px; flex:0 0 auto; border:0; border-radius:999px; padding:0; cursor:pointer;
  background:rgba(255,255,255,.12); color:#f4f2ee; display:grid; place-items:center;
}
.cap-v-x--ghost{visibility:hidden; pointer-events:none;}
.cap-v-count{display:flex; align-items:center; gap:10px; font-size:var(--fs-label); color:rgba(244,242,238,.7);}
.cap-v-count b{color:#f4f2ee; font-size:calc(var(--fs-label) * 1.25);}
.cap-v-tag{font-size:11px; font-weight:600; padding:3px 9px; border-radius:999px; background:rgba(255,255,255,.14); color:#f4f2ee;}
.cap-v-tag--new{background:rgba(52,168,83,.25); color:#7fd69a;}
.cap-v-tag--pack{background:rgba(139,92,246,.28); color:#c4b0ff;}
/* The board: the photo is a rounded card in the middle, the neighbours' edges peek in either side. */
.cap-v-stage{flex:1; min-height:0; position:relative; touch-action:none; overflow:hidden;}
.cap-v-track{position:absolute; top:14px; bottom:6px; left:14%; width:72%; will-change:transform; backface-visibility:hidden;}
.cap-v-slide{position:absolute; top:0; bottom:0; width:100%; display:flex; align-items:center; justify-content:center; container-type:size; opacity:.35; will-change:opacity; transform:translateZ(0);}
.cap-v-slide--prev{justify-content:flex-end;}
.cap-v-slide--next{justify-content:flex-start;}
.cap-v-slide--cur{opacity:1; z-index:1;}
.cap-v-slide img{height:auto; object-fit:cover; border-radius:16px; display:block; -webkit-user-drag:none;}
.cap-v-slide--cur img{transform-origin:50% 50%; will-change:transform;}
.cap-v-soft{filter:blur(6px);}
/* Removing: the photo shrinks away, then a short "Photo removed". */
.cap-v-slide--cur{transition:transform .22s ease-in, opacity .22s ease-in;}
.cap-v-track--out .cap-v-slide--cur{transform:scale(.82); opacity:0;}
.cap-v-said{
  position:absolute; left:50%; top:50%; transform:translate(-50%,-50%); z-index:2; pointer-events:none;
  display:flex; align-items:center; gap:8px; padding:10px 16px; border-radius:999px;
  background:rgba(20,22,21,.9); color:#f4f2ee; font-size:var(--fs-label); font-weight:600;
  animation:capsaid .9s ease forwards;
}
@keyframes capsaid{0%{opacity:0; transform:translate(-50%,-50%) scale(.9);}15%{opacity:1; transform:translate(-50%,-50%) scale(1);}75%{opacity:1;}100%{opacity:0;}}
.cap-pt{transition:transform .22s ease-in, opacity .22s ease-in;}
.cap-pt--out{transform:scale(.5); opacity:0;}
.cap-spill--said span{background:rgba(20,22,21,.9);}
.cap-v-dots{flex:0 0 auto; display:flex; justify-content:center; flex-wrap:wrap; gap:6px; padding:12px var(--col) 14px;}
.cap-v-dots i{width:6px; height:6px; border-radius:99px; background:rgba(255,255,255,.28); transition:width .18s;}
.cap-v-dots i.on{width:18px; background:#f4f2ee;}
.cap-v-strip{
  flex:0 0 auto; position:relative; display:flex; align-items:center; gap:8px; overflow-x:auto; scrollbar-width:none;
  padding:4px var(--col);
}
.cap-v-strip::-webkit-scrollbar{display:none;}
/* Centred while it fits, scrolls once it does not. */
.cap-v-cell{display:flex; align-items:center; gap:8px; flex:0 0 auto;}
.cap-v-cell:first-child{margin-left:auto;}
.cap-v-cell:last-child{margin-right:auto;}
.cap-v-div{width:1px; height:30px; background:rgba(255,255,255,.25); margin:0 2px;}
.cap-v-th{
  flex:0 0 auto; width:44px; height:56px; padding:0; border:0; border-radius:8px; overflow:hidden; cursor:pointer;
  background:rgba(255,255,255,.14); opacity:.5; outline:2px solid transparent; outline-offset:2px;
}
.cap-v-th.on{opacity:1; outline-color:#f4f2ee;}
.cap-v-th img{width:100%; height:100%; object-fit:cover; display:block;}
.cap-v-act{flex:0 0 auto; min-height:44px; padding:18px var(--col) calc(env(safe-area-inset-bottom,0px) + 16px); display:flex;}
.cap-v-rm{
  flex:1; display:flex; align-items:center; justify-content:center; gap:8px; min-height:48px;
  border:0; border-radius:999px; cursor:pointer; font-family:inherit; font-size:var(--fs-btn); font-weight:600;
  background:rgba(255,255,255,.12); color:#f4f2ee;
}
.cap-v-dim{position:absolute; inset:0; z-index:2; display:flex; align-items:flex-end; background:rgba(0,0,0,.6);}
.cap-v-sheet{
  width:100%; box-sizing:border-box; display:grid; grid-template-columns:auto 1fr; gap:6px 14px; align-items:center;
  padding:18px var(--col) calc(env(safe-area-inset-bottom,0px) + 18px); border-radius:20px 20px 0 0; background:#1c1f1d;
  animation:capsheet .22s ease-out;
}
.cap-v-sheet img{width:44px; height:58px; border-radius:8px; object-fit:cover; background:rgba(255,255,255,.14);}
.cap-v-sheet-t{display:flex; flex-direction:column; gap:2px; font-size:var(--fs-label); color:rgba(244,242,238,.7);}
.cap-v-sheet-t strong{font-size:var(--fs-btn); color:#f4f2ee;}
.cap-v-sheet-b{grid-column:1 / -1; display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-top:12px;}
.cap-v-sheet-b button{
  min-height:48px; border:0; border-radius:999px; cursor:pointer; font-family:inherit; font-size:var(--fs-btn); font-weight:600; color:#fff;
}
.cap-v-no{background:rgba(255,255,255,.12);}
.cap-v-yes{background:#c4362c;}
@keyframes capsheet{from{transform:translateY(100%);}to{transform:none;}}
@media (prefers-reduced-motion: reduce){.cap-v-sheet{animation:none;} .cap-v-slide{transition:none;} .cap-v-slide--cur{transition:none;}
  .cap-v-said{animation:none;} .cap-pt{transition:none;} .cap-pt--out{transform:none;}}

/* --- footer ------------------------------------------------------------- */
.cap-foot{
  position:absolute; left:0; right:0; bottom:0; z-index:3;
  display:flex; flex-direction:column; gap:10px;
  padding:10px 0 calc(env(safe-area-inset-bottom,0px) + 14px);
  background:linear-gradient(to top,rgba(0,0,0,.86),rgba(0,0,0,0));
}
/* A tile is not decoration - it is the control you tap to throw a bad
   photograph away, and it carries its own delete button. Both scale together
   so the delete stays hittable at every viewport. */
/* ⚠️ THE TILE IS THE SHAPE OF THE PHOTOGRAPH, and that is the fix for the
   report "in the frame some portion was coming, but when I saw the images
   those top/bottom portions were not there".
   It was a SQUARE box with object-fit:cover. Measured on the lab across four
   viewports: a 9:16 shot showed 100% of its width and 56% of its height - 22%
   off the top and 22% off the bottom - and a 16:9 shot lost 22% off each side.
   Nothing was wrong with the file; this is the first place a merchant looks at
   a shot, at the shelf, and it was hiding nearly half of it. The gallery grid
   had the identical bug and the identical numbers, and its fix note quotes the
   earlier form of the same complaint ("some images look cropped from the top").
   --shot-ar is the frame's own aspect, written by the same measure() that
   writes the capture outline, so the tile and the capture area cannot drift
   apart. A portrait tile is NARROWER than the old square, so this costs the
   viewfinder nothing - it gives the strip room for more of them.
   The 44px floor keeps a very tall frame's tile tappable, and contain is the
   backstop: a shot taken before the stream renegotiated letterboxes rather
   than quietly losing an edge. */
.cap-tile{
  position:relative; flex:0 0 auto;
  height:var(--tile);
  width:max(44px, calc(var(--tile) * var(--shot-ar, 1)));
  border-radius:12px; overflow:hidden; background:#1c211f;
  border:1px solid rgba(255,255,255,.28);
}
/* The whole tile is the hit target for opening it. Reset to nothing, because a
   UA button brings a border, a background and a font that would all show. */
.cap-tile-open{
  position:absolute; inset:0; padding:0; border:0; background:none;
  cursor:pointer; display:block; width:100%; height:100%;
}
.cap-tile img{width:100%; height:100%; object-fit:contain; display:block;}
/* Honest about what it is: a preview frame standing in until the real capture
   lands. Dimmed rather than hidden, because the merchant took that photograph
   and should see it immediately - just not mistake it for finished. */
.cap-tile--pending img{opacity:.5; filter:saturate(.7);}
@keyframes capspin{ to{transform:rotate(360deg)} }
.cap-shutter{
  width:var(--shutter); height:var(--shutter); border-radius:999px; padding:0;
  background:#fff; border:calc(var(--shutter) * .055) solid rgba(255,255,255,.34);
  background-clip:padding-box; cursor:pointer;
}
.cap-shutter:disabled{opacity:.32; cursor:default;}
.cap-shutter:active:not(:disabled){transform:scale(.94);}
@keyframes cappulse{
  0%{box-shadow:0 0 0 0 rgba(25,195,155,.85);}
  100%{box-shadow:0 0 0 28px rgba(25,195,155,0);}
}

.cap-error{
  margin:0 var(--pad); padding:calc(var(--pad) * .55) calc(var(--pad) * .75);
  border-radius:8px; background:rgba(156,40,30,.94); color:#fff;
  font-size:var(--fs-label); line-height:1.35;
}

/* --- full-stage states -------------------------------------------------- */
.cap-panel{
  position:absolute; inset:0; z-index:4; display:flex; flex-direction:column;
  align-items:center; justify-content:center; gap:var(--pad); text-align:center;
  padding:calc(var(--pad) * 2) calc(var(--pad) * 1.5); background:#0b0f0e;
}
.cap-panel h2{
  font-size:var(--fs-title); font-weight:600; margin:0 0 4px;
  letter-spacing:-.025em; line-height:1.05;
}
/* 40ch, and NOT text-wrap:balance. Balance shortens every line to even them
   up, which on a phone turns one sentence into five stubby ones with the
   measure half empty on both sides. Let it fill the column. */
.cap-panel p{
  font-size:var(--fs-body); line-height:1.5; color:rgba(255,255,255,.74);
  margin:0; max-width:40ch;
}
.cap-panel .cap-btn{margin-top:calc(var(--pad) * .8);}
/* Quick scan's saved screen: a full dark page, no camera. */
.cap-saved{
  position:absolute; inset:0; z-index:30; background:#121211; color:#f4f2ee;
  display:flex; flex-direction:column; align-items:center; gap:8px;
  padding:96px 28px 0; text-align:center; animation:cap-sfade .16s ease-out;
}
.cap-saved-pic{
  width:220px; height:220px; flex:none; margin-bottom:16px; border-radius:24px; overflow:hidden;
  background:#1c1b19 center/cover; border:1px dashed #55534e; box-sizing:border-box;
  animation:cap-sscale .16s ease-out;
}
.cap-saved-pic:has(img){border:0;}
.cap-saved-pic img{width:100%; height:100%; object-fit:cover; display:block;}
.cap-saved-pic--pack{background:linear-gradient(180deg,#fff,#f3f1ec);}
.cap-saved-pic--pack img{object-fit:contain;}
.cap-saved-pill.cap-saved-pill--desk{background:rgba(138,180,248,.16); color:#8ab4f8;}
.cap-saved-pill{
  display:inline-flex; align-items:center; gap:6px; height:28px; padding:0 12px; border-radius:14px;
  background:rgba(127,214,154,.14); color:#7fd69a; font-size:13px; font-weight:600;
}
.cap-saved-name{
  font-size:20px; line-height:26px; font-weight:650; overflow-wrap:anywhere;
  display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden;
}
.cap-saved-line{display:inline-flex; align-items:center; gap:8px; font-size:13px; color:#a8a39a;}
.cap-saved-spin{
  width:14px; height:14px; border-radius:50%; border:2px solid #55534e; border-top-color:#d9d5cd;
  box-sizing:border-box; animation:capspin .7s linear infinite;
}
.cap-saved-foot{
  position:absolute; left:20px; right:20px; bottom:34px;
  display:flex; flex-direction:column; gap:4px;
}
.cap-saved-go{
  box-sizing:border-box; height:52px; border-radius:26px; background:#f2b01e; color:#1b1a18;
  font:inherit; font-size:15px; font-weight:700; border:0; cursor:pointer; text-decoration:none;
  display:flex; align-items:center; justify-content:center;
}
.cap-saved-more{
  align-self:center; height:44px; padding:0 12px; background:none; border:0; cursor:pointer;
  font:inherit; font-size:14px; color:#d9d5cd;
}
@keyframes cap-sscale{from{transform:scale(.96);} to{transform:none;}}
@media (prefers-reduced-motion:reduce){
  .cap-saved, .cap-saved-pic{animation:none;}
  .cap-saved-spin{animation:none;}
}
.cap-queue{
  margin:calc(var(--pad) * .35) 0 0;
  padding:calc(var(--pad) * .3) calc(var(--pad) * .5);
  border-radius:8px;
  background:rgba(0,0,0,.45); color:#CFEFE4;
  font-size:var(--fs-label); line-height:1.35;
  display:flex; align-items:center; gap:.6em; justify-content:space-between;
}
.cap-queue--bad{background:rgba(120,53,15,.85); color:#FDE9C8;}
.cap-queue button{
  flex:0 0 auto; border:0; border-radius:6px; cursor:pointer;
  background:#FDE9C8; color:#3B2607; font-family:inherit;
  font-size:var(--fs-label); font-weight:600;
  padding:calc(var(--pad) * .25) calc(var(--pad) * .6);
}
.cap-panel code{
  font-family:ui-monospace,monospace; font-size:13px;
  background:rgba(255,255,255,.1); padding:1px 5px; border-radius:3px;
}
@media (prefers-reduced-motion:reduce){
  .cap-shutter:active:not(:disabled){transform:none;}
}
`;
export default function CaptureCamera({ onBack, preset = null, quick = null, bulkReturnUrl = null, desk = false, trainingShop = null, trainingDailyMb = 0 }) {
  const {
    addFiles,
    addMore,
    allowTip,
    camAsks,
    autoReturn,
    awaitingRef,
    backdropRef,
    barcode,
    blConfirm,
    blView,
    bulk,
    bulkEdit,
    bulkLast,
    bulkPill,
    cam,
    camGone,
    camHelp,
    camLiveUi,
    camState,
    canConfirm,
    cancelTyping,
    checking,
    choose,
    closeBulk,
    confirmCapture,
    deciding,
    deskEnd,
    discard,
    discardAsk,
    discardGo,
    doneSnap,
    dropTraining,
    dup,
    dupGoRef,
    fileShotsRef,
    filedToast,
    finishEnd,
    finishing,
    flash,
    flipCamera,
    flipTypeMode,
    flipping,
    footRef,
    hideAllowTip,
    iconBtn,
    ident,
    inspecting,
    isBulk,
    isNewItem,
    isPreset,
    leaveQuick,
    leaveScan,
    live,
    noBarcode,
    offlineBulk,
    onFileCand,
    openTyping,
    phase,
    photoInRef,
    photoSub,
    photoTiles,
    photoTitle,
    photoUi,
    preScan,
    presetStuck,
    presetTitle,
    queue,
    queueRef,
    quickMode,
    readPhoto,
    reading,
    removeSaved,
    removeShot,
    resetToScanning,
    resume,
    resumeCamera,
    reticleRef,
    saveError,
    savedUi,
    saving,
    scanUi,
    setAwaiting,
    setBlConfirm,
    setBlView,
    setDeciding,
    setDiscardAsk,
    setDup,
    setFinishing,
    setIdentity,
    setInspecting,
    setPage,
    setResume,
    setShots,
    setTyped,
    sheetChose,
    shots,
    shotsRef,
    snap,
    snapMirror,
    snapMode,
    snapRef,
    stageRef,
    startCamera,
    submitTyped,
    takeMore,
    takeShot,
    tapCamera,
    tilesRef,
    toggleTorch,
    topRef,
    torchOn,
    typeAbc,
    typeInRef,
    typed,
    typing,
    videoRef,
  } = useCaptureFlow({ onBack, preset, quick, bulkReturnUrl, desk, trainingShop, trainingDailyMb });

  return (
    <div className={"cap-root" + (inspecting ? " cap-root--viewing" : "")}>
      {/* dangerouslySetInnerHTML, not {CAPTURE_CSS}, and it is not optional:
          as CHILDREN React escapes the text on the server and not on the
          client, so a single apostrophe in a CSS comment renders as
          "browser&#x27;s" in the server HTML and "browser's" on hydration.
          That mismatch fails hydration for the WHOLE document and React
          silently throws the server render away and rebuilds it client-side.
          It is why SCANNER_CSS and SPREADSHEET_CSS are injected this way too. */}
      <style dangerouslySetInnerHTML={{ __html: CAPTURE_CSS }} />

      <div className={"cap-stage" + (scanUi || deciding || checking ? " cap-stage--scan" : "") + (photoUi && !deciding ? " cap-stage--photo" : "") + (camState === "none" ? " cap-stage--none" : "")} ref={stageRef}>
        <CameraStage
          backdropRef={backdropRef}
          cam={cam}
          camLiveUi={camLiveUi}
          checking={checking}
          deciding={deciding}
          live={live}
          phase={phase}
          scanUi={scanUi}
          snapMirror={snapMirror}
          snapMode={snapMode}
          snapRef={snapRef}
          tapCamera={tapCamera}
          videoRef={videoRef}
        />



        {live || preScan || scanUi ? (
          <>
            <div
              ref={topRef}
              className={
                "cap-top" + (phase === "locked" && !photoUi && !checking ? " cap-top--locked" : "") + (scanUi || photoUi || checking ? " cap-top--scan" : "") + (photoUi ? " cap-top--photo" : "") + (deciding ? " cap-top--hide" : "")
              }
            >
            <BarcodeBar
              bulkReturnUrl={bulkReturnUrl}
              cam={cam}
              cancelTyping={cancelTyping}
              checking={checking}
              flipCamera={flipCamera}
              flipping={flipping}
              iconBtn={iconBtn}
              leaveScan={leaveScan}
              quick={quick}
              quickMode={quickMode}
              scanUi={scanUi}
              toggleTorch={toggleTorch}
              torchOn={torchOn}
              typing={typing}
            />
            <PhotoBar
              cam={cam}
              discard={discard}
              flipCamera={flipCamera}
              flipping={flipping}
              iconBtn={iconBtn}
              isPreset={isPreset}
              photoSub={photoSub}
              photoTitle={photoTitle}
              photoUi={photoUi}
              saving={saving}
              scanUi={scanUi}
              toggleTorch={toggleTorch}
              torchOn={torchOn}
            />
            {isBulk && photoUi && bulkPill.tone !== "none" ? <div className="cap-bpgap" aria-hidden="true" /> : null}
            {/* THE SHOP LINE THAT USED TO SIT HERE IS GONE, and with it the
                third and last version of a sentence that kept turning out to be
                untrue: "nothing is uploaded or saved", then "Lab — filed to the
                test store", then "Filed to <shop> · enrichment runs later".
                Capture never enqueues a run and nothing drains the pile, so the
                last one was a promise nothing kept.

                Removed rather than corrected, on the merchant's own judgement
                that an amber bar over the viewfinder was costing more than it
                told anyone.

                ⚠️ WHAT THIS COSTS, stated rather than glossed: the gallery names
                the shop, but this screen is NOT always reached through it.
                App.jsx picks the screen from the pathname, so a reload, a
                restored tab or a bookmarked /camera renders the camera first
                and the gallery never mounts — and on that path nothing on
                screen says which shop the work goes to. On 2026-09-11 counts
                meant for one shop were written to another for want of exactly
                that. If this bites, the answer is a shop line somewhere that
                does not sit over the viewfinder, not this one back. */}
            {/* THE UPLOADER, AND IT ONLY SPEAKS WHEN IT HAS SOMETHING TO SAY.
                A permanent "0 uploading" is noise on a screen read in
                peripheral vision; a failure that says nothing is how a morning
                goes missing. So: silent when idle, a count while working, and
                an amber row that does not go away while anything is stuck. */}
            {isBulk && bulkPill.tone !== "none" ? null : queue.failed > 0 ? (
              <div className="cap-queue cap-queue--bad" role="alert">
                <span>
                  Upload stuck ({queue.pending} waiting). Your photos are safe.
                </span>
                <button type="button" onClick={() => queueRef.current.retry()}>
                  Retry
                </button>
              </div>
            ) : queue.pending > 0 ? (
              <div className="cap-queue">
                Uploading {queue.pending} in the background
                {queue.current ? ` · ${queue.current}` : ""}
              </div>
            ) : null}
            </div>

            <BulkPillSlot
              blView={blView}
              bulk={bulk}
              bulkLast={bulkLast}
              bulkPill={bulkPill}
              checking={checking}
              deciding={deciding}
              isBulk={isBulk}
              photoUi={photoUi}
              queue={queue}
              queueRef={queueRef}
              scanUi={scanUi}
              setBlView={setBlView}
            />

            <CameraOverlay
              allowTip={allowTip}
              camAsks={camAsks}
              camHelp={camHelp}
              camState={camState}
              cancelTyping={cancelTyping}
              hideAllowTip={hideAllowTip}
              reticleRef={reticleRef}
              scanUi={scanUi}
              tapCamera={tapCamera}
              typing={typing}
            />
            {checking ? <div className="cap-spill cap-pfade"><span>&#10003; {barcode || ident?.barcode || "Checking"}</span></div> : null}

            {flash ? <div className="cap-flash" aria-hidden="true" /> : null}
            {snap ? <div className="cap-snap" aria-hidden="true" /> : null}

            {filedToast && !preset && !isBulk ? (
              <div className={"cap-filed" + (scanUi || photoUi ? " cap-filed--scan" : "")} role="status">
                <span className="cap-check">&#10003;</span>
                Filed <b>{filedToast.barcode}</b> &middot; {filedToast.count}{" "}
                {filedToast.count === 1 ? "photo" : "photos"}
              </div>
            ) : null}

            <footer className={"cap-foot" + (scanUi || checking ? " cap-foot--scan" : "") + (photoUi ? " cap-foot--photo" : "")} ref={footRef}>
              <BarcodeFooter
                camState={camState}
                flipTypeMode={flipTypeMode}
                noBarcode={noBarcode}
                openTyping={openTyping}
                photoInRef={photoInRef}
                readPhoto={readPhoto}
                reading={reading}
                saveError={saveError}
                scanUi={scanUi}
                setTyped={setTyped}
                submitTyped={submitTyped}
                typeAbc={typeAbc}
                typeInRef={typeInRef}
                typed={typed}
                typing={typing}
              />

              <PhotoFooter
                addFiles={addFiles}
                canConfirm={canConfirm}
                confirmCapture={confirmCapture}
                fileShotsRef={fileShotsRef}
                isNewItem={isNewItem}
                offlineBulk={offlineBulk}
                photoTiles={photoTiles}
                photoUi={photoUi}
                removeShot={removeShot}
                saveError={saveError}
                savedUi={savedUi}
                saving={saving}
                setInspecting={setInspecting}
                shots={shots}
                takeShot={takeShot}
                tilesRef={tilesRef}
              />


            </footer>
          </>
        ) : null}

        {/* CHECKING A SHOT DOES NOT INTERRUPT THE SHOOT.
            This is an overlay, not a screen: the camera keeps its stream, the
            barcode stays locked, and the deck is untouched. Closing puts the
            merchant back exactly where they were, still on the same product.
            Tearing the camera down and rebuilding it to show a still would
            cost a second of black on every check and would risk the stream not
            coming back, which this surface has already been through once. */}
        {deciding && ident?.status === "decide" ? (
          <BarcodeOnFileSheet
            ident={ident}
            quickMode={quickMode}
            onSel={(i) => setIdentity({ ...ident, sel: i })}
            onPick={(c) => {
              sheetChose("pick");
              setDeciding(false);
              choose(c);
            }}
            onNew={() => {
              sheetChose("new");
              setDeciding(false);
              choose(null);
            }}
            onClose={() => {
              sheetChose("close");
              if (shots.length) setDeciding(false);
              else void resetToScanning();
            }}
          />
        ) : null}

        <BulkSheets
          addMore={addMore}
          blConfirm={blConfirm}
          blView={blView}
          bulk={bulk}
          bulkEdit={bulkEdit}
          closeBulk={closeBulk}
          discardAsk={discardAsk}
          discardGo={discardGo}
          dup={dup}
          dupGoRef={dupGoRef}
          finishEnd={finishEnd}
          finishing={finishing}
          isBulk={isBulk}
          leaveQuick={leaveQuick}
          leaveScan={leaveScan}
          queue={queue}
          queueRef={queueRef}
          quickMode={quickMode}
          resume={resume}
          setBlConfirm={setBlConfirm}
          setBlView={setBlView}
          setDiscardAsk={setDiscardAsk}
          setDup={setDup}
          setFinishing={setFinishing}
          setResume={setResume}
          shots={shots}
        />




        <PhotoInspect
          inspecting={inspecting}
          photoTiles={photoTiles}
          removeSaved={removeSaved}
          removeShot={removeShot}
          setInspecting={setInspecting}
        />

        <CameraPanels
          barcode={barcode}
          cam={cam}
          camGone={camGone}
          camHelp={camHelp}
          ident={ident}
          phase={phase}
          preScan={preScan}
          presetTitle={presetTitle}
          resumeCamera={resumeCamera}
          shots={shots}
          startCamera={startCamera}
        />

        {deskEnd ? <DeskEnd n={deskEnd.n} /> : null}

        <SavedScreen
          autoReturn={autoReturn}
          awaitingRef={awaitingRef}
          barcode={barcode}
          doneSnap={doneSnap}
          dropTraining={dropTraining}
          ident={ident}
          isPreset={isPreset}
          onBack={onBack}
          onFileCand={onFileCand}
          phase={phase}
          preset={preset}
          presetStuck={presetStuck}
          presetTitle={presetTitle}
          queueRef={queueRef}
          quick={quick}
          savedUi={savedUi}
          setAwaiting={setAwaiting}
          setPage={setPage}
          setShots={setShots}
          shots={shots}
          shotsRef={shotsRef}
          takeMore={takeMore}
        />





      </div>
    </div>
  );
}
